<template>
  <PageLayout title="周公解梦">
      <!-- 解梦区域 -->
      <MysticalCard class="input-section">
        <h3 class="section-title">请描述您的梦境</h3>
        <div class="input-area">
          <textarea 
            v-model="dreamContent"
            placeholder="例如：我梦见自己在飞翔..."
            rows="4"
            class="mystical-textarea"
          ></textarea>
          <MysticalButton 
            @click="handleDreamAnalysis"
            :disabled="!dreamContent || isAnalyzing"
            class="analyze-button"
          >
            {{ isAnalyzing ? '正在解析...' : '解析梦境' }}
          </MysticalButton>
          <p v-if="isAnalyzing" class="loading-fun-msg">{{ currentLoadingMsg }}</p>
        </div>
      </MysticalCard>

      <!-- 解梦结果区域 -->
      <div class="analysis-grid" v-if="hasAnalysis">
        <div class="dream-share-row" style="grid-column: 1 / -1; text-align:center; margin-bottom:4px">
          <button @click="shareVisible = true" style="background:#e60023;color:#fff;border:0;border-radius:999px;padding:11px 28px;font-size:15px;font-weight:700;cursor:pointer">
            📣 分享我的解梦
          </button>
        </div>
        <!-- 心理学分析 -->
        <AIResultCard
          title="弗洛伊德心理分析"
          subtitle="从心理学角度解读梦境寓意"
          icon="psychology"
          :content="psychologyAnalysis"
          :loading="isAnalyzing"
          :status="isAnalyzing ? '分析中...' : '分析完成'"
        />

        <!-- 传统解梦 -->
        <AIResultCard
          title="周公解梦"
          subtitle="从传统文化角度解读梦境"
          icon="traditional"
          :content="traditionalAnalysis"
          :loading="isAnalyzing"
          :status="isAnalyzing ? '分析中...' : '分析完成'"
        />
      </div>

    <ShareResult v-model:visible="shareVisible" scene-label="AI智能解梦"
      :headline="dreamHeadline"
      :subline="'周公解梦 × 弗洛伊德 双重解读'"
      :tags="['周公解梦', '心理学', '潜意识']"
      page="dream" />
    <PayDialog
      v-model:visible="showPayDialog"
      :cost="payDialogCost"
      :balance="payDialogBalance"
      :feature="payDialogFeature"
      :rmb-price="payDialogRmbPrice"
      @goPoints="goToPoints"
    />
  </PageLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import { ShareResult } from '@/components/common'
import MysticalCard from '@/components/common/MysticalCard.vue'
import MysticalButton from '@/components/common/MysticalButton.vue'
import AIResultCard from '@/components/common/AIResultCard.vue'
import { analyzeDream } from '@/services/ai/dream'
import { ElMessage } from 'element-plus'
import { API_CONFIG } from '@/config'
import { useAuthGuard } from '@/composables/useAuthGuard'
import { getLoadingMessages } from '@/data/loadingMessages'
import PayDialog from '@/components/common/PayDialog.vue'
import { usePaymentGuard } from '@/composables/usePaymentGuard'

// 记录交互的辅助函数 - 修复：确保正确记录完整数据
const logInteraction = async (type, question, result, interpretation) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('[DreamView] 未登录，跳过记录交互')
      return // 未登录不记录
    }

    console.log('[DreamView] 记录交互:', { 
      type, 
      questionLength: question?.length,
      hasResult: !!result,
      hasInterpretation: !!interpretation 
    })

    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.persona.interaction}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type,
        question,
        result, // 包含分析结果
        interpretation
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    console.log('[DreamView] 交互记录成功')
  } catch (error) {
    // 静默失败，不影响用户体验
    console.error('[DreamView] 记录交互失败:', error)
  }
}

const dreamContent = ref('')
const shareVisible = ref(false)
const dreamHeadline = computed(() => {
  const t = (dreamContent.value || '').trim()
  if (!t) return '我做了一个奇怪的梦'
  return t.length > 18 ? `我梦见了「${t.slice(0, 16)}…」` : `我梦见了「${t}」`
})
const psychologyAnalysis = ref('')
const traditionalAnalysis = ref('')
const isAnalyzing = ref(false)
const { requireLogin } = useAuthGuard()
const { showPayDialog, payDialogCost, payDialogBalance, payDialogFeature, payDialogRmbPrice, checkFeatureCost, goToPoints } = usePaymentGuard()

// 加载文案轮播
const loadingMsgs = ref([])
const currentLoadingMsg = ref('')
let loadingMsgTimer = null
function startLoadingMessages() {
  loadingMsgs.value = getLoadingMessages('dream', 8)
  currentLoadingMsg.value = loadingMsgs.value[0]
  let idx = 0
  loadingMsgTimer = setInterval(() => {
    idx = (idx + 1) % loadingMsgs.value.length
    currentLoadingMsg.value = loadingMsgs.value[idx]
  }, 2500)
}
function stopLoadingMessages() {
  if (loadingMsgTimer) { clearInterval(loadingMsgTimer); loadingMsgTimer = null }
}

// 计算属性：是否有分析结果
const hasAnalysis = computed(() => {
  return psychologyAnalysis.value || traditionalAnalysis.value
})

// 处理解梦分析
const handleDreamAnalysis = async () => {
  if (!requireLogin('dream')) return
  console.log('[DreamView] 按钮点击 - 检查状态:', {
    hasContent: !!dreamContent.value,
    contentLength: dreamContent.value?.length,
    isAnalyzing: isAnalyzing.value
  })

  if (!dreamContent.value || isAnalyzing.value) {
    console.log('[DreamView] 退出分析:', !dreamContent.value ? '内容为空' : '正在分析中')
    return
  }

  // 保存当前梦境内容，防止在异步过程中被修改
  const currentDream = dreamContent.value.trim()

  try {
    console.log('[DreamView] 开始解梦分析流程...')

    // 积分守卫：动态查价（feature-cost，含会员折扣/免费额度），查价失败放行由后端扣费兜底
    const canProceed = await checkFeatureCost('dream', 'AI 解梦')
    if (!canProceed) return

    isAnalyzing.value = true
    startLoadingMessages()
    console.log('[DreamView] 状态更新: isAnalyzing = true')

    // 清空之前的结果
    psychologyAnalysis.value = ''
    traditionalAnalysis.value = ''
    console.log('[DreamView] 清空之前的分析结果')

    // 开始分析
    console.log('[DreamView] 准备调用 analyzeDream...')
    await analyzeDream(
      currentDream,
      // 心理学分析回调
      (content) => {
        console.log('[DreamView] 心理分析回调被触发:', {
          contentLength: content.length,
          content: content.slice(0, 50) + '...'
        })
        psychologyAnalysis.value += content
      },
      // 传统解梦回调
      (content) => {
        console.log('[DreamView] 传统解梦回调被触发:', {
          contentLength: content.length,
          content: content.slice(0, 50) + '...'
        })
        traditionalAnalysis.value += content
      }
    )

    console.log('[DreamView] 解梦分析完成', {
      psychologyLength: psychologyAnalysis.value.length,
      traditionalLength: traditionalAnalysis.value.length,
      hasAnalysis: hasAnalysis.value
    })

    // 修复：在分析完成后立即记录交互，传递完整的分析结果
    // 使用 await 确保记录完成（但不阻塞用户体验）
    const analysisResult = {
      psychology: psychologyAnalysis.value,
      traditional: traditionalAnalysis.value,
      timestamp: new Date().toISOString()
    }

    // 异步记录，不阻塞UI
    logInteraction('dream', currentDream, analysisResult, {
      psychology: psychologyAnalysis.value,
      traditional: traditionalAnalysis.value
    }).catch(err => {
      console.error('[DreamView] 记录交互异常:', err)
    })

  } catch (error) {
    console.error('[DreamView] 解梦分析失败:', {
      error,
      message: error.message,
      stack: error.stack
    })
    if (error.message && error.message.includes('积分不足')) return
    ElMessage.error(error.message || '解梦分析失败，请稍后重试')
  } finally {
    console.log('[DreamView] 状态更新: isAnalyzing = false')
    isAnalyzing.value = false
    stopLoadingMessages()
  }
}
</script>

<style scoped>

.input-section {
  margin-bottom: var(--spacing-xl);
}

.section-title {
  color: var(--color-primary);
  text-align: center;
  margin-bottom: var(--spacing-lg);
  font-size: 1.5rem;
}

.input-area {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.mystical-textarea {
  width: 100%;
  padding: var(--spacing-lg);
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
  min-height: 120px;
  transition: all var(--transition-normal);
}

.mystical-textarea:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 10px var(--color-primary-shadow);
}

.analyze-button {
  align-self: flex-end;
  min-width: 120px;
}

.loading-fun-msg {
  color: var(--color-text-light);
  font-size: 0.85rem;
  text-align: center;
  margin-top: 12px;
  animation: fadeInOut 2.5s ease-in-out infinite;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
}

/* 响应式布局 */
@media (max-width: 768px) {
  .analysis-grid {
    grid-template-columns: 1fr;
  }

  .mystical-textarea {
    height: 120px;
    min-height: 80px;
    padding: var(--spacing-md);
    font-size: 0.95rem;
  }

  .analysis-grid :deep(.ai-result-card) {
    padding: var(--spacing-md);
  }
}
 
@media (max-width: 400px) {
  .section-title {
    font-size: 1.2rem;
  }

  .analysis-grid :deep(.ai-result-card) {
    padding: var(--spacing-sm);
  }

  .analysis-grid :deep(.ai-result-card .result-content) {
    font-size: 0.9rem;
  }
}
</style>